import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  LayoutDashboard, Map, Leaf, Sprout, Package,
  ShoppingCart, LogOut, Plug, Store, Droplets,
  CloudSun, BarChart2, Settings, Shield, ClipboardList,
  Tractor, PawPrint, FlaskConical, Wifi, Waves,
  FileCheck, Layers, Dna, TrendingUp, TrendingDown, Receipt, FolderKanban,
  FileSpreadsheet, BookOpen, Users, Grid3x3, DollarSign, Menu, X, Sun, Moon, Trash2, CalendarDays, Gauge, UserCog, Boxes, LifeBuoy, Container, CalendarClock, Megaphone, Plane,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAccessibility } from '../../lib/accessibility'
import { useAuthStore } from '../../lib/store'
import { commerceApi, authApi } from '../../lib/api'
import { MangoMark } from './MangoMark'
import { OfflineIndicator } from './OfflineIndicator'
import { FeedbackWidget } from './FeedbackWidget'
import { UpsellGate } from './UpsellGate'

// Tracks whether the viewport is phone/small-tablet sized so the shell can switch
// from an inline sidebar (desktop) to a slide-in drawer (mobile). Without this the
// fixed 196px sidebar ate half a phone screen and content was unusable.
function useIsMobile(breakpoint = 820) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`)
    const handler = () => setIsMobile(mq.matches)
    handler()
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [breakpoint])
  return isMobile
}

// ── Grafty design tokens (matching design-system.css) ─────────────────────
// Theme-reactive: values resolve to the active accent theme AND light/dark mode
// (design-system.css). Inline styles accept var()/color-mix() strings directly.
const C = {
  bg:         'var(--color-bg)',
  bgRaised:   'var(--color-bg-card)',
  border:     'var(--color-border)',
  amber:      'var(--color-primary)',
  green:      'var(--color-accent)',
  text:       'var(--color-text)',
  textMuted:  'var(--color-text-muted)',
  textFaint:  'var(--color-text-faint)',
  hover:      'var(--color-bg-hover)',
  active:     'color-mix(in srgb, var(--color-primary) 12%, transparent)',
}

// Light/dark switch — flips the surface palette; the accent theme is unchanged.
function ModeToggle({ compact = false }: { compact?: boolean }) {
  const { prefs, updatePref } = useAccessibility()
  const dark = prefs.mode !== 'light'
  return (
    <button
      onClick={() => updatePref('mode', dark ? 'light' : 'dark')}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8,
        color: C.textMuted, cursor: 'pointer',
        ...(compact
          ? { width: 34, height: 34, padding: 0 }
          : { width: '100%', padding: '7px 10px', fontSize: 12, fontWeight: 600 }),
      }}
    >
      {dark ? <Sun size={16} /> : <Moon size={16} />}
      {!compact && <span>{dark ? 'Light mode' : 'Dark mode'}</span>}
    </button>
  )
}

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
}

interface NavGroup {
  section: string
  items: NavItem[]
}

const navGroups: NavGroup[] = [
  {
    section: 'FARM',
    items: [
      { to: '/',         icon: LayoutDashboard, label: 'Dashboard'  },
      { to: '/activity', icon: ClipboardList,   label: 'Activity'   },
      { to: '/calendar', icon: CalendarDays,    label: 'Calendar'   },
      { to: '/map',      icon: Map,             label: 'Farm Map'   },
      { to: '/drone-maps', icon: Plane,         label: 'Drone Maps' },
    ],
  },
  {
    section: 'GROWING',
    items: [
      { to: '/plants',      icon: Sprout,    label: 'Plants'      },
      { to: '/blocks',      icon: Grid3x3,   label: 'Blocks'      },
      { to: '/cohorts',     icon: Boxes,     label: 'Cohorts'     },
      { to: '/varieties',   icon: Leaf,      label: 'Varieties'   },
      { to: '/harvest',      icon: BarChart2,    label: 'Harvest'       },
      { to: '/ripening',     icon: Gauge,        label: 'Ripening'      },
      { to: '/spoilage',     icon: Trash2,       label: 'Spoilage'      },
      { to: '/containers',   icon: Container,    label: 'Containers'    },
      { to: '/yield-impact', icon: TrendingDown, label: 'Yield Impact'   },
      { to: '/reports',      icon: TrendingUp,   label: 'Reports'       },
      { to: '/valuation',    icon: DollarSign,   label: 'Valuation'     },
      { to: '/propagation', icon: Dna,       label: 'Propagation' },
      { to: '/nursery',     icon: Sprout,    label: 'Nursery'     },
      { to: '/seeds',       icon: Layers,    label: 'Seeds'       },
    ],
  },
  {
    section: 'OPERATIONS',
    items: [
      { to: '/equipment',  icon: Tractor,  label: 'Equipment'  },
      { to: '/labor',      icon: Users,    label: 'Picking Labor' },
      { to: '/team',       icon: UserCog,  label: 'Team'       },
      { to: '/animals',    icon: PawPrint, label: 'Animals'    },
      { to: '/forms',      icon: FileSpreadsheet, label: 'Custom Forms' },
      { to: '/irrigation', icon: Droplets, label: 'Irrigation' },
    ],
  },
  {
    section: 'MONITORING',
    items: [
      { to: '/weather',   icon: CloudSun,    label: 'Weather'   },
      { to: '/sensors',   icon: Wifi,        label: 'Sensors'   },
      { to: '/lab-tests', icon: FlaskConical, label: 'Lab Tests' },
    ],
  },
  {
    section: 'RESOURCES',
    items: [
      { to: '/inventory', icon: Package, label: 'Inventory'     },
      { to: '/soil',      icon: Layers,  label: 'Soil & Compost'},
      { to: '/water',     icon: Waves,   label: 'Water & Wells' },
    ],
  },
  {
    section: 'COMMERCE',
    items: [
      { to: '/commerce',    icon: ShoppingCart, label: 'Commerce'    },
      { to: '/preorders',   icon: CalendarClock, label: 'Pre-orders' },
      { to: '/customers',   icon: Users,        label: 'Customers'   },
      { to: '/marketing',   icon: Megaphone,    label: 'Marketing'   },
      { to: '/vendors',     icon: Store,        label: 'Vendors'     },
      { to: '/accounting',  icon: Receipt,       label: 'Accounting'  },
      { to: '/expenses',    icon: DollarSign,    label: 'Expenses'    },
      { to: '/projects',    icon: FolderKanban,  label: 'Projects'    },
    ],
  },
  {
    section: 'BUSINESS',
    items: [
      { to: '/permits',      icon: FileCheck,       label: 'Permits & Docs'  },
      { to: '/ag-exemption', icon: FileSpreadsheet, label: 'Ag Exemption'    },
      { to: '/integrations', icon: Plug,            label: 'Integrations'    },
      { to: '/audit',        icon: Shield,          label: 'Audit Log'       },
    ],
  },
  {
    section: 'HELP',
    items: [
      { to: '/docs',    icon: BookOpen, label: 'Help & Docs' },
      { to: '/support', icon: LifeBuoy, label: 'Support'      },
    ],
  },
]

// navKey turns a nav label into its i18n key: "Farm Map" → "farm_map",
// "Soil & Compost" → "soil_compost". Keeps the nav array unchanged.
const navKey = (s: string) => s.toLowerCase().replace(/&/g, ' ').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')

export function AppLayout() {
  const { tenant, role, clearAuth } = useAuthStore()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isSuperuser = role === 'superuser'
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Collapse the drawer whenever we return to a desktop width.
  useEffect(() => { if (!isMobile) setDrawerOpen(false) }, [isMobile])
  const closeDrawer = () => setDrawerOpen(false)

  // New-order badge: count of unprocessed storefront orders (status='reserved').
  // Superusers have no tenant context, so skip the fetch for them.
  const { data: commerceOrders } = useQuery({
    queryKey: ['commerce-orders'],
    queryFn: () => commerceApi.listOrders().then(r => r.data),
    enabled: !isSuperuser,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  const newOrderCount = (commerceOrders ?? []).filter(o => !o.status || o.status === 'reserved').length
  const navBadges: Record<string, number> = { '/commerce': newOrderCount }

  function handleLogout() {
    // Revoke the refresh token server-side (§208) before dropping local auth.
    // Fire-and-forget — sign out regardless of the network result.
    authApi.logout().catch(() => {})
    clearAuth()
    localStorage.removeItem('refresh_token')
    navigate('/login')
  }

  const linkStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '6px 10px',
    borderRadius: 7,
    fontSize: 12,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? C.amber : C.textMuted,
    background: isActive ? C.active : 'transparent',
    textDecoration: 'none',
    transition: 'background 0.12s, color 0.12s',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
  })

  // On mobile, tapping a nav item should also dismiss the drawer.
  const onNavClick = isMobile ? closeDrawer : undefined

  return (
    <div style={{ display: 'flex', height: '100dvh', background: C.bg, fontFamily: '"DM Sans", system-ui, sans-serif' }}>

      {/* ── Mobile top bar (hamburger + logo) ───────────────────────── */}
      {isMobile && (
        <header style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 60,
          height: 'calc(52px + env(safe-area-inset-top, 0px))',
          boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0 12px',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingLeft: 'max(12px, env(safe-area-inset-left, 0px))',
          paddingRight: 'max(12px, env(safe-area-inset-right, 0px))',
          background: C.bgRaised, borderBottom: `1px solid color-mix(in srgb, var(--color-primary) 16%, transparent)`,
        }}>
          <button
            onClick={() => setDrawerOpen(v => !v)}
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            style={{ background: 'transparent', border: 'none', color: C.text, cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center' }}
          >
            {drawerOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <MangoMark size={28} />
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif', letterSpacing: '-0.01em' }}>
            Grafty
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ModeToggle compact />
            <div style={{ fontSize: 11, color: C.textFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>
              {tenant?.name ?? (isSuperuser ? 'Superuser' : '')}
            </div>
          </div>
        </header>
      )}

      {/* ── Drawer backdrop (mobile, when open) ─────────────────────── */}
      {isMobile && drawerOpen && (
        <div
          onClick={closeDrawer}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 45 }}
        />
      )}

      {/* ── Sidebar (inline on desktop, slide-in drawer on mobile) ──── */}
      <aside style={{
        display: 'flex', flexDirection: 'column',
        width: 196,
        background: `linear-gradient(180deg, ${C.bgRaised}, ${C.bg})`,
        borderRight: `1px solid ${C.border}`,
        boxShadow: 'inset 0 2px 0 color-mix(in srgb, var(--color-primary) 35%, transparent)',
        flexShrink: 0,
        ...(isMobile ? {
          position: 'fixed' as const, top: 'calc(52px + env(safe-area-inset-top, 0px))', left: 0, bottom: 0, zIndex: 50,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.22s ease',
        } : {}),
      }}>

        {/* Logo (desktop only — the mobile top bar carries it) */}
        {!isMobile && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '14px 14px 12px',
            borderBottom: '1px solid color-mix(in srgb, var(--color-primary) 16%, transparent)',
          }}>
            <MangoMark size={36} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif', letterSpacing: '-0.01em' }}>
                Grafty
              </div>
              <div style={{ fontSize: 11, color: C.textFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>
                {tenant?.name ?? (isSuperuser ? 'Superuser' : '—')}
              </div>
            </div>
          </div>
        )}

        {/* Grouped nav */}
        <nav style={{ flex: 1, padding: '8px 6px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {navGroups.map(({ section, items }) => (
            <div key={section} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.textFaint, letterSpacing: '0.08em', padding: '6px 10px 4px', textTransform: 'uppercase' }}>
                {t('navSection.' + section.toLowerCase(), section)}
              </div>
              {items.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  onClick={onNavClick}
                  style={({ isActive }) => linkStyle(isActive)}
                  onMouseEnter={e => { const el = e.currentTarget as HTMLElement; if (el.getAttribute('aria-current') !== 'page') el.style.background = C.hover }}
                  onMouseLeave={e => { const el = e.currentTarget as HTMLElement; if (el.getAttribute('aria-current') !== 'page') el.style.background = 'transparent' }}
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={13} style={{ flexShrink: 0, opacity: isActive ? 1 : 0.65 }} />
                      {t('nav.' + navKey(label), label)}
                      {navBadges[to] > 0 && (
                        <span style={{
                          marginLeft: 'auto', flexShrink: 0, minWidth: 16, height: 16,
                          padding: '0 5px', borderRadius: 8, background: C.amber, color: C.bg,
                          fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', lineHeight: 1,
                        }}>{navBadges[to]}</span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Footer — admin link + settings + sign out */}
        <div style={{ padding: '6px 6px 10px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <div style={{ padding: '2px 4px 6px' }}><ModeToggle /></div>
          {isSuperuser && (
            <NavLink
              to="/admin"
              onClick={onNavClick}
              style={({ isActive }) => linkStyle(isActive)}
            >
              {({ isActive }) => (
                <>
                  <Shield size={13} style={{ flexShrink: 0, color: isActive ? C.amber : '#F87171', opacity: isActive ? 1 : 0.85 }} />
                  <span style={{ color: isActive ? C.amber : '#F87171' }}>Admin Portal</span>
                </>
              )}
            </NavLink>
          )}
          <NavLink
            to="/settings"
            onClick={onNavClick}
            style={({ isActive }) => linkStyle(isActive)}
          >
            {({ isActive }) => (
              <>
                <Settings size={13} style={{ flexShrink: 0, opacity: isActive ? 1 : 0.65 }} />
                Settings
              </>
            )}
          </NavLink>
          <button onClick={handleLogout} style={{ ...linkStyle(false), background: 'transparent' }}>
            <LogOut size={13} style={{ flexShrink: 0, opacity: 0.65 }} />
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main content ───────────────────────────────────────────── */}
      <main style={{ flex: 1, minWidth: 0, overflowY: 'auto', background: C.bg, ...(isMobile ? { paddingTop: 'calc(52px + env(safe-area-inset-top, 0px))', paddingBottom: 'env(safe-area-inset-bottom, 0px)' } : {}) }}>
        <Outlet />
      </main>

      {/* Connectivity + offline-outbox status, visible on every authed route. */}
      <OfflineIndicator />

      {/* Send-feedback widget (§221) — tenant users only (files a feedback support ticket). */}
      {!isSuperuser && <FeedbackWidget />}

      {/* Plant-limit upgrade modal (§242) — tenant users only. Self-gating: shows
          proactively when sustained ≥2 days at the cap, or reactively on a 402. */}
      {!isSuperuser && <UpsellGate />}
    </div>
  )
}
